import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { overtimeAPI } from "../../lib/api";
import { formatHours, LIEU_STATUS_STYLES } from "../../lib/overtime";
import { Send } from "lucide-react";
import { toast } from "sonner";

export function TimeInLieu({ requests, balance, onChanged }) {
  const [form, setForm] = useState({ hours: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const hours = parseFloat(form.hours);
    if (!hours || hours <= 0) {
      toast.error("Enter a valid number of hours");
      return;
    }
    if (!form.reason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setSubmitting(true);
    try {
      await overtimeAPI.requestLieu({ hours, reason: form.reason.trim() });
      toast.success("Time in lieu request submitted");
      setForm({ hours: "", reason: "" });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Request Time in Lieu</CardTitle>
          <CardDescription>
            Available balance: <span className="font-semibold text-slate-800">{formatHours(balance)}</span>. Approved hours are deducted from your overtime total.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-sm">Hours requested</Label>
            <Input
              type="number"
              min="0.5"
              step="0.5"
              className="mt-1 max-w-[160px]"
              placeholder="e.g. 8"
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: e.target.value })}
              data-testid="lieu-hours-input"
            />
          </div>
          <div>
            <Label className="text-sm">Reason</Label>
            <Textarea
              className="mt-1"
              rows={3}
              placeholder="e.g. Taking Friday afternoon off in lieu of weekend work"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              data-testid="lieu-reason-input"
            />
          </div>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="lieu-submit-button">
            <Send className="w-4 h-4 mr-2" />
            {submitting ? "Submitting..." : "Submit Request"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">My Requests</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="lieu-my-requests">
          {requests.length === 0 ? (
            <p className="text-sm text-slate-500">No time in lieu requests yet.</p>
          ) : (
            requests.map((r) => (
              <div key={r.id} className="p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid={`lieu-request-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{formatHours(r.hours)}</span>
                  <Badge className={`${LIEU_STATUS_STYLES[r.status]} hover:bg-opacity-100 capitalize`} data-testid={`lieu-status-${r.id}`}>
                    {r.status}
                  </Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1">{r.reason}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Requested {new Date(r.requested_date).toLocaleDateString("en-AU")}
                  {r.decided_by && ` • ${r.status} by ${r.decided_by}`}
                  {r.decision_note && ` — "${r.decision_note}"`}
                </p>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
